import { LitElement, html, css, render } from 'lit';
import { icons } from '../../utilities/icons';
import { msg } from '@lit/localize';

import { accessibility,formStyles} from '../../utilities/styles';
export class AllInMultiselect extends LitElement {
    static properties = {
        label: { type: String },
        name: { type: String },
        list: { type: String },
        activeDescendant: { type: String, state: true },
        value: { type: Array},
        _value: { type: Array, state: true },
        selected: { type: Array, state: true },
        required: { type: Boolean},
        errormessage: { type: String},
        description: { type: String },
        tooltip: { type: String },
        isinvalid: { type: Boolean, reflect: true },
    }
    constructor() {
        super();
    }

    async connectedCallback() {
        super.connectedCallback();
        this.options = [];
        if(this.list){
            try {
                this.options = JSON.parse(this.list);
            } catch (e) {
                console.error('Error parsing list:', e);
            }
        }
        this._value = this.value ?? [];
        this.selected = this.value?.map(item=>this.options.find(option=>option.name===item)?.label)?? [];
    }

    get listboxNode() {
        return this.renderRoot.querySelector('#ss_elem_list');
    }

    createRenderRoot() { 
        return this;
    }

    render(){
        return html`
           <div class="listbox-area ${this.isinvalid?'error':''}">
                <input type="hidden" name="${this.name}" id="${this.name}" .value="${this._value.join(',')}" data-type="multiselect" data-label="${this.label}" data-valuelabels="${this.selected.join(', ')}"/>
                <div>
                    ${this.label?html`<strong id="${this.name}_label" class="listbox-label" >${this.label == 'Type'?'Event':''} ${this.label}${this.required?'*':''}</strong>`:''}
                    <p class="small">${this.description || msg('Select one or more options from the list below.')}</p>
                    ${this.tooltip?html`<allin-tooltip tooltip="${this.tooltip}"></allin-tooltip>`:''}
                    <ul id="ss_elem_list" tabindex="0" role="listbox" aria-labelledby="${this.name}_label" aria-multiselectable="true" @click=${this.checkClickItem} @keydown=${this.checkKeyPress} @focus=${this.setupFocus} aria-invalid="${this.isInvalid}" aria-required="${this.required}"">
                        ${this.options.map((option) => {
                            return html`
                            <li id="ss_elem_${option.name}" role="option" data-value="${option.name}" data-label="${option.label}" aria-selected="${this._value.includes(option.name)}">
                                <span class="checkmark" aria-hidden="true"></span>
                                ${option.label}
                            </li>`}
                        )}
                    </ul>
                </div>
                <div>

                    <ul class="selection">
                        ${this.selected.length?html`<h4>${msg('Selected')}</h4>`:''}
                        ${this.selected.map((item) => html`
                            <li class="selected-item">
                                ${item}
                            </li>
                        `)}
                    </ul>
                    <div class="error-message" ?hidden="${!this.isinvalid}">
                        ${this.dataset.error_message || msg('Please select at least one option.')}
                    </div>
                </div>
            </div>
        `;
    }

    setupFocus() {
        if (this.activeDescendant) {
            const listitem = this.renderRoot.querySelector(`#${this.activeDescendant}`);
            listitem.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        } else {
            this.focusFirstItem();
        }
    }

    focusFirstItem() {
        var firstItem = this.listboxNode.querySelector('[role="option"]');

        if (firstItem) {
            this.focusItem(firstItem);
        }
    }

    focusLastItem() {
        const itemList = this.listboxNode.querySelectorAll('[role="option"]');

        if (itemList.length) {
            this.focusItem(itemList[itemList.length - 1]);
        }
    }

    checkKeyPress(evt) {
        const lastActiveId = this.activeDescendant;
        const allOptions = this.listboxNode.querySelectorAll('[role="option"]');
        const currentItem =
        this.listboxNode.querySelector(`#${this.activeDescendant}`) || allOptions[0];
        let nextItem = currentItem;

        if (!currentItem) {
        return;
        }

        switch (evt.key) {
       
        case 'ArrowUp':
        case 'ArrowDown':
            evt.preventDefault();
            if (!this.activeDescendant) {
            // focus first option if no option was previously focused, and perform no other actions
                this.focusItem(currentItem);
            break;
            }
            
            if (evt.key === 'ArrowUp') {
                nextItem = this.findPreviousOption(currentItem);
            } else {
                nextItem = this.findNextOption(currentItem);
            }
            if (nextItem) {
                this.focusItem(nextItem);
            }

            break;
        case ' ':
            evt.preventDefault();
            this.toggleSelectItem(nextItem);
            break;
        default:
            if (evt.key.length === 1) {
            const itemToFocus = this.findItemToFocus(evt.key.toLowerCase());
            if (itemToFocus) {
                this.focusItem(itemToFocus);
            }
            }
            break;
        }

        if (this.activeDescendant !== lastActiveId) {
            this.updateScroll();
        }
    }

    /* Return the next listbox option, if it exists; otherwise, returns null */
    findNextOption(currentOption) {
        const allOptions = Array.prototype.slice.call(
        this.listboxNode.querySelectorAll('[role="option"]')
        ); // get options array
        const currentOptionIndex = allOptions.indexOf(currentOption);
        let nextOption = null;

        if (currentOptionIndex > -1 && currentOptionIndex < allOptions.length - 1) {
        nextOption = allOptions[currentOptionIndex + 1];
        }

        return nextOption;
    }

    /* Return the previous listbox option, if it exists; otherwise, returns null */
    findPreviousOption(currentOption) {
        const allOptions = Array.prototype.slice.call(
        this.listboxNode.querySelectorAll('[role="option"]')
        ); // get options array
        const currentOptionIndex = allOptions.indexOf(currentOption);
        let previousOption = null;

        if (currentOptionIndex > -1 && currentOptionIndex > 0) {
        previousOption = allOptions[currentOptionIndex - 1];
        }

        return previousOption;
    }

    checkClickItem(evt) {
        if (evt.target.getAttribute('role') !== 'option') {
        return;
        }

        this.focusItem(evt.target);
        this.toggleSelectItem(evt.target);
        this.updateScroll();
    }

    /**
     * @description
     *  Toggle the aria-selected value
     * @param element
     *  The element to select
     */
    toggleSelectItem(element) {
        element.setAttribute(
            'aria-selected',
            element.getAttribute('aria-selected') === 'true' ? 'false' : 'true'
        );
        let dataValue = element.dataset.value;
        if(!dataValue) return;
        if(this._value.includes(dataValue)){
            this._value = this._value.filter(item => item !== dataValue);
        } 
        else {
            this._value = [...this._value,dataValue];
        }
        let dataLabel = element.dataset.label;
        if(!dataLabel) return;
        if(this.selected.includes(dataLabel)){
            this.selected = this.selected.filter(item => item !== dataLabel);
        }
        else {
            this.selected = [...this.selected, dataLabel];
        }

    }

  
    defocusItem(element) {
        if (!element) {
        return;
        }
        element.classList.remove('focused');
    }

  /**
   * @description
   *  Focus on the specified item
   * @param element
   *  The element to focus
   */
    focusItem(element) {
        this.defocusItem(this.listboxNode.querySelector(`#${this.activeDescendant}`));
        element.classList.add('focused');
        this.activeDescendant = element.id;
        this.isinvalid = false;
        this.checkUpDownButtons();
    }

  /**
   * Check if the selected option is in view, and scroll if not
   */
    updateScroll() {
        const selectedOption = this.listboxNode.querySelector(`#${this.activeDescendant}`);
        if (selectedOption) {
        const scrollBottom =
            this.listboxNode.clientHeight + this.listboxNode.scrollTop;
        const elementBottom =
            selectedOption.offsetTop + selectedOption.offsetHeight;
        if (elementBottom > scrollBottom) {
            this.listboxNode.scrollTop =
            elementBottom - this.listboxNode.clientHeight;
        } else if (selectedOption.offsetTop < this.listboxNode.scrollTop) {
            this.listboxNode.scrollTop = selectedOption.offsetTop;
        }
        selectedOption.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
    }

  /**
   * @description
   *  Enable/disable the up/down arrows based on the activeDescendant.
   */
    checkUpDownButtons() {
        const activeElement = this.listboxNode.querySelector(`#${this.activeDescendant}`);

        if (!this.moveUpDownEnabled) {
        return;
        }

        if (!activeElement) {
        this.upButton.setAttribute('aria-disabled', 'true');
        this.downButton.setAttribute('aria-disabled', 'true');
        return;
        }

        if (this.upButton) {
        if (activeElement.previousElementSibling) {
            this.upButton.setAttribute('aria-disabled', false);
        } else {
            this.upButton.setAttribute('aria-disabled', 'true');
        }
        }

        if (this.downButton) {
        if (activeElement.nextElementSibling) {
            this.downButton.setAttribute('aria-disabled', false);
        } else {
            this.downButton.setAttribute('aria-disabled', 'true');
        }
        }
    }

 
  clearActiveDescendant() {
    this.activeDescendant = null;
    this.listboxNode.setAttribute('aria-activedescendant', null);

    this.updateMoveButton();
    this.checkUpDownButtons();
  }

  updateValueExternally(newValue){
    this._value = newValue ?? [];
    this.selected = newValue?.map(item=>this.options.find(option=>option.name===item)?.label)?? [];
    this.requestUpdate();
  }



static styles = [
    accessibility,
    formStyles,
    css`
        .group{
            display:none;
        }
        
        
    `
]
}


